import React, { useState } from 'react';
import { authService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import { ShieldCheck, Lock, CheckCircle2, AlertTriangle } from 'lucide-react';

const ChangePassword: React.FC = () => {
  const [form, setForm] = useState({ old: '', new: '', confirm: '' });
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { logout } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (form.new.length < 8) {
      showToast('Complexity fail: Min 8 characters', 'error');
      return;
    }
    if (form.new !== form.confirm) {
      showToast('Sequence mismatch: New keys must match', 'error');
      return;
    }

    setIsLoading(true);
    const response = await authService.changePassword({
      currentPassword: form.old,
      newPassword: form.new
    });

    if (response.success) {
      showToast(response.message || 'Password changed successfully', 'success');
      // Force logout as per contract
      setTimeout(() => logout(), 2000);
    } else {
      showToast(response.message || 'Change password failed', 'error');
    }
    setIsLoading(false);
  };

  return (
    <div className="max-w-xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="space-y-1">
        <h1 className="text-3xl font-black text-white tracking-tight uppercase italic">Security Protocol</h1>
        <p className="text-brand-muted text-xs font-bold uppercase tracking-widest opacity-70 flex items-center gap-2">
          <ShieldCheck size={14} className="text-brand-primary" />
          Active Session Key Rotation
        </p>
      </div>

      <div className="glass-card p-10 rounded-[3rem] shadow-2xl relative overflow-hidden">
        <form onSubmit={handleSubmit} className="space-y-6">
          <Input
            label="Current Secure Key"
            type="password"
            placeholder="••••••••"
            value={form.old}
            onChange={(e) => setForm({ ...form, old: e.target.value })}
            required
          />
          <div className="h-[1px] bg-white/5 w-full"></div>
          <Input
            label="New Access Protocol"
            type="password"
            placeholder="Min 8 characters"
            value={form.new}
            onChange={(e) => setForm({ ...form, new: e.target.value })}
            required
          />
          <Input
            label="Confirm New Protocol"
            type="password"
            placeholder="••••••••"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            required
          />

          <div className="pt-4">
            <Button type="submit" className="w-full h-16" isLoading={isLoading}>
              COMMIT KEY ROTATION
            </Button>
          </div>
        </form>
      </div>

      <div className="bg-brand-primary/5 border border-brand-primary/10 p-6 rounded-2xl">
        <p className="text-[9px] text-brand-muted uppercase font-black tracking-[0.2em] leading-relaxed">
          Security Note: Changing your terminal keys will NOT affect your vault address but will invalidate all other active mobile and desktop session tokens for this node.
        </p>
      </div>
    </div>
  );
};

export default ChangePassword;
